import { apiInstance, gql } from "@/services/httpAdapter";

// Commons Fragments
import { InputValueFragment, LongevityFragment, TopPerformerBonusFragment } from "@PayPlans/api/fragments/commons";

export async function createTechnicianPayPlan({ locationId, position }) {
	const query = gql`
		mutation createTechnicianPayPlan($locationId: String!, $position: String!) {
			createTechnicianPayPlan(locationId: $locationId, position: $position) {
				name
				createdAt
			}
		}
	`;

	const variables = { locationId, position };

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.createTechnicianPayPlan || null;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function saveNewTechnicianPayPlan({ locationId, payPlan, position }) {
	const query = gql`
		mutation saveNewTechnicianPayPlan($locationId: String!, $input: SaveTechnicianPayPlan!, $position: String!) {
			saveNewTechnicianPayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				default {
					_id
					defaultValue {
						input
						value
					}
					isGuaranteedDraw
				}
				flaggedHoursBonus {
					_id
					tiers {
						_id
						from {
							...InputValue
						}
						to {
							...InputValue
						}
						commissions {
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commission {
								...InputValue
							}
						}
					}
					applyTiersRetroactively
				}
				nightShiftPay {
					_id
					daysSelected
					offset
					from
					to
					isMultipleDays
					commission {
						input
						value
					}
					prorateByTimeCard
				}
				holidayPay {
					_id
					holidaysSelected {
						localHolidayName
						holidayName
						date
					}
					holidayByType
				}
				retentionPay {
					retention {
						input
						value
					}
				}
				topTechniciansBonus {
					...TopPerformerBonus
				}
				targetedPerformanceBonus {
					_id
					departments {
						tiers {
							_id
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commissions {
								from {
									...InputValue
								}
								to {
									...InputValue
								}
								commission {
									...InputValue
								}
							}
						}
						selectedDepartment
						selectedPayType
						applyTiersRetroactively
						component
					}
					operationCodes {
						tiers {
							_id
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commissions {
								from {
									...InputValue
								}
								to {
									...InputValue
								}
								commission {
									...InputValue
								}
							}
						}
						applyTiersRetroactively
						selectedPayType
						selectedOperationCodes {
							_id
							code
							type
							pay
						}
						selectedTimeLapse
						component
					}
				}
				longevity {
					...Longevity
				}
				locked
				available
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${LongevityFragment}
		${InputValueFragment}
		${TopPerformerBonusFragment}
	`;

	const variables = {
		locationId,
		position,
		input: payPlan,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.saveNewTechnicianPayPlan;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function cloneTechnicianPayPlan({ locationId, information, actualPosition }) {
	const { _id, createdBy, position } = information;
	const query = gql`
		mutation cloneTechnicianPayPlan($input: ClonePayPlan!, $locationId: String!, $position: String!) {
			cloneTechnicianPayPlan(input: $input, locationId: $locationId, position: $position) {
				_id
				locationId
				name
				position
				default {
					_id
					defaultValue {
						input
						value
					}
					isGuaranteedDraw
				}
				flaggedHoursBonus {
					_id
					tiers {
						_id
						from {
							...InputValue
						}
						to {
							...InputValue
						}
						commissions {
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commission {
								...InputValue
							}
						}
					}
					applyTiersRetroactively
				}
				nightShiftPay {
					_id
					daysSelected
					offset
					from
					to
					isMultipleDays
					commission {
						input
						value
					}
					prorateByTimeCard
				}
				holidayPay {
					_id
					holidaysSelected {
						localHolidayName
						holidayName
						date
					}
					holidayByType
				}
				retentionPay {
					retention {
						input
						value
					}
				}
				topTechniciansBonus {
					...TopPerformerBonus
				}
				targetedPerformanceBonus {
					_id
					departments {
						tiers {
							_id
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commissions {
								from {
									...InputValue
								}
								to {
									...InputValue
								}
								commission {
									...InputValue
								}
							}
						}
						selectedDepartment
						selectedPayType
						applyTiersRetroactively
						component
					}
					operationCodes {
						tiers {
							_id
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commissions {
								from {
									...InputValue
								}
								to {
									...InputValue
								}
								commission {
									...InputValue
								}
							}
						}
						applyTiersRetroactively
						selectedPayType
						selectedOperationCodes {
							_id
							code
							type
							pay
						}
						selectedTimeLapse
						component
					}
				}
				longevity {
					...Longevity
				}
				locked
				available
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${LongevityFragment}
		${InputValueFragment}
		${TopPerformerBonusFragment}
	`;

	const variables = {
		locationId,
		input: {
			createdBy,
			position,
			payPlanId: _id,
		},
		position: actualPosition,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.cloneTechnicianPayPlan || null;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function updateTechnicianPayPlan({ locationId, payPlan, position }) {
	const query = gql`
		mutation updateTechnicianPayPlan($locationId: String!, $input: UpdateTechnicianPayPlanInput!, $position: String!) {
			updateTechnicianPayPlan(input: $input, locationId: $locationId, position: $position) {
				_id
				locationId
				name
				position
				default {
					_id
					defaultValue {
						input
						value
					}
					isGuaranteedDraw
				}
				flaggedHoursBonus {
					_id
					tiers {
						_id
						from {
							...InputValue
						}
						to {
							...InputValue
						}
						commissions {
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commission {
								...InputValue
							}
						}
					}
					applyTiersRetroactively
				}
				nightShiftPay {
					_id
					daysSelected
					offset
					from
					to
					isMultipleDays
					commission {
						input
						value
					}
					prorateByTimeCard
				}
				holidayPay {
					_id
					holidaysSelected {
						localHolidayName
						holidayName
						date
					}
					holidayByType
				}
				retentionPay {
					retention {
						input
						value
					}
				}
				topTechniciansBonus {
					...TopPerformerBonus
				}
				targetedPerformanceBonus {
					_id
					departments {
						tiers {
							_id
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commissions {
								from {
									...InputValue
								}
								to {
									...InputValue
								}
								commission {
									...InputValue
								}
							}
						}
						applyTiersRetroactively
						selectedDepartment
						selectedPayType
						component
					}
					operationCodes {
						tiers {
							_id
							from {
								...InputValue
							}
							to {
								...InputValue
							}
							commissions {
								from {
									...InputValue
								}
								to {
									...InputValue
								}
								commission {
									...InputValue
								}
							}
						}
						applyTiersRetroactively
						selectedPayType
						selectedOperationCodes {
							_id
							code
							type
							pay
						}
						selectedTimeLapse
						component
					}
				}
				longevity {
					...Longevity
				}
				locked
				available
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${LongevityFragment}
		${InputValueFragment}
		${TopPerformerBonusFragment}
	`;

	const variables = {
		locationId,
		position,
		input: payPlan,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateTechnicianPayPlan || null;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function updateTechnicianAvailablePayPlan({ locationId, information, position }) {
	const { _id, updatedBy, available } = information;
	const query = gql`
		mutation updateTechnicianAvailablePayPlan($locationId: String!, $input: UpdateAvailablePayPlanInput!, $position: String!) {
			updateTechnicianAvailablePayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				available
			}
		}
	`;

	const variables = {
		locationId,
		position,
		input: {
			_id,
			updatedBy,
			available,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateTechnicianAvailablePayPlan;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function deleteTechnicianPayPlan({ locationId, _id, position }) {
	const query = gql`
		mutation deleteTechnicianPayPlan($locationId: String!, $id: String!, $position: String!) {
			deleteTechnicianPayPlan(id: $id, locationId: $locationId, position: $position) {
				_id
				locationId
			}
		}
	`;

	const variables = {
		locationId,
		position,
		id: _id,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deleteTechnicianPayPlan || null;
	} catch (error) {
		console.error(error);
		return null;
	}
}
